// Parity with the Docs::PdfPageTemplate enum is enforced by spec/models/docs/pdf_page_template_spec.rb.
export const Orientation = {
  Unknown: 'unknown',
  Portrait: 'portrait',
  Landscape: 'landscape',
  Square: 'square',
  Mixed: 'mixed',
} as const;

export type Orientation = (typeof Orientation)[keyof typeof Orientation];

export interface PageUsage {
  documentTemplateId: number;
  documentTemplateName: string;
  contextType: string;
  contextId: number;
  contextName: string | null;
  inPageLibrary: boolean;
  inDefaultPages: boolean;
  position: number | null;
  lastPage: boolean;
}

export interface PageRecord {
  id: number;
  templateType: string;
  name: string;
  userLabel: string | null;
  customLabel: string | null;
  tags: string[];
  // Set by Buildout, like POWER PAGE; shown but can't be removed
  lockedTags: string[];
  createdByName: string | null;
  sizeLabel: string | null;
  // PDF pages only; the file a replacement swaps out.
  fileName: string | null;
  // PDF pages only; false when the page is visible here but owned by another context.
  ownedByContext: boolean | null;
  orientation: Orientation;
  createdAt: string | null;
  updatedAt: string | null;
  documentsCount: number;
  documents: PageUsage[];
  // In a Market Overviews collection, which only lists here when a search finds it. The slot is then its only
  // way into documents.
  inPageCollection: boolean;
  marketName: string | null;
}

export interface MarketOverviewsSummary {
  pageCount: number;
  slotDocumentCount: number;
}

export interface FilterOption {
  value: string;
  label: string;
}

export interface DocumentFilterOption {
  value: number;
  label: string;
}

export interface FilterOptions {
  pageTypes: FilterOption[];
  orientations: FilterOption[];
  createdBy: FilterOption[];
  documents: DocumentFilterOption[];
  tags: FilterOption[];
}

// Rows, selections and bulk results all identify a page by type and id.
export const pageKey = (templateType: string, id: number) => `${templateType}-${id}`;

// Mirrors DocumentDefaults::PageLibraryBulk::DONE / SKIPPED.
export const BulkStatus = { Done: 'done', Skipped: 'skipped' } as const;

export type BulkStatus = (typeof BulkStatus)[keyof typeof BulkStatus];

export interface BulkResult {
  pageType: string;
  pageId: number;
  name: string | null;
  status: BulkStatus;
  reason: string | null;
}

export const TemplateType = {
  Tree: 'Docs::PageTemplateTree',
  Pdf: 'Docs::PdfPageTemplate',
  UserPage: 'Docs::UserPageTemplate',
} as const;

export type TemplateType = (typeof TemplateType)[keyof typeof TemplateType];

export const PAGE_TYPE_PARAMS: Record<string, string> = {
  [TemplateType.Tree]: 'tree',
  [TemplateType.Pdf]: 'pdf',
  [TemplateType.UserPage]: 'user_page',
};

export type UsageFilter = 'used' | 'not_used' | null;

export type SortDirection = 'asc' | 'desc';

export interface SortState {
  name: string;
  direction: SortDirection;
}

export interface ColumnConfig {
  key: string;
  label: string;
  sortKey?: string;
}
